/**
 * Libreria dei test di misura: elenco di società, nel contesto della squadra
 * aperta.
 *
 * I test stanno in una card sola, su due colonne quando la vista è larga,
 * dieci righe per pagina, con ricerca, metodo e stato sopra: stanno nella
 * query string, così un elenco filtrato si ricarica e si condivide com'è. Un
 * test lo svolge chiunque, qualunque attributo valuti.
 * Admin, Mister e Dirigente leggono; Mister e Dirigente creano ed eseguono i
 * test attivi sulla squadra aperta; il nuovo si crea in un modale.
 */

import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ETICHETTA_METODO_TEST, METODI_TEST, puo } from '@calcio/shared';
import { RulerIcon, SearchIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { PulsanteNuovo } from '../comuni/Azioni.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { Filtro, TUTTI } from '../comuni/FiltriVista.js';
import { usePaginazione } from '../comuni/Paginazione.js';
import { useDati } from '../comuni/dati.js';
import { useContesto } from '../navigazione/contesto.js';
import { DialogTest } from './DialogTest.js';
import { RigaTest } from './RigaTest.js';
import type { TestInElenco } from './test-misura.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

/** Righe per pagina: da `@5xl` stanno su due colonne, cinque per colonna. */
const PER_PAGINA = 10;

/** La chiave della pagina nell'URL: un filtro nuovo la azzera. */
const PAGINA = 'pagina';

export function ElencoTest() {
  const { ss } = useParams<{ ss: string }>();
  return <Elenco key={ss} ss={ss!} />;
}

function Elenco({ ss }: { ss: string }) {
  const { utente } = useContesto();
  const { dati, errore } = useDati<TestInElenco[]>('/test');
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const metodo = params.get('metodo') ?? TUTTI;
  const stato = params.get('stato') ?? TUTTI;
  const scrive = puo(utente.assegnazioni, 'misurazione.scrivi', ss);

  const imposta = (chiave: string, valore: string) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (valore && valore !== TUTTI) next.set(chiave, valore);
        else next.delete(chiave);
        next.delete(PAGINA);
        return next;
      },
      { replace: true },
    );

  const visibili = useMemo(() => {
    if (!dati) return [];
    const testo = q.trim().toLocaleLowerCase('it');
    return dati.filter((t) => {
      if (metodo !== TUTTI && !t.misure.some((m) => m.metodo === metodo)) return false;
      if (stato === 'attivi' && !t.attivo) return false;
      if (stato === 'disattivati' && t.attivo) return false;
      if (testo && !t.nome.toLocaleLowerCase('it').includes(testo)) return false;
      return true;
    });
  }, [dati, q, metodo, stato]);

  const [nuovoAperto, setNuovoAperto] = useState(false);
  const nuovo = scrive && (
    <PulsanteNuovo etichetta="Nuovo test" onClick={() => setNuovoAperto(true)} />
  );
  const modale = scrive && (
    <DialogTest ss={ss} aperto={nuovoAperto} onChiudi={() => setNuovoAperto(false)} />
  );

  if (!dati) {
    return (
      <>
        <Testata titolo="Test di misura" voce="test" />
        <Corpo>
          <StatoCaricamento errore={errore} />
        </Corpo>
      </>
    );
  }

  return (
    <>
      <Testata titolo="Test di misura" voce="test" azione={dati.length > 0 && nuovo} />
      {modale}
      <Corpo>
        {dati.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>Nessun test</EmptyTitle>
              <EmptyDescription>
                {scrive
                  ? 'Un test è un esercizio che dà un punteggio da 1 a 100 agli attributi che valuta: lo sprint sui 30 metri, il salto in lungo, la navetta a livelli.'
                  : 'Mister e Dirigente non hanno ancora creato test.'}
              </EmptyDescription>
            </EmptyHeader>
            {nuovo && <EmptyContent>{nuovo}</EmptyContent>}
          </Empty>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <InputGroup className="w-full @xl:max-w-sm">
                <InputGroupInput
                  placeholder="Cerca per nome"
                  aria-label="Cerca per nome"
                  value={q}
                  onChange={(e) => imposta('q', e.target.value)}
                />
                <InputGroupAddon>
                  <SearchIcon />
                </InputGroupAddon>
              </InputGroup>
              <Filtro
                etichetta="Metodo di calcolo"
                valore={metodo}
                onCambia={(v) => imposta('metodo', v)}
                opzioni={[
                  { valore: TUTTI, etichetta: 'Tutti' },
                  ...METODI_TEST.map((m) => ({ valore: m, etichetta: ETICHETTA_METODO_TEST[m] })),
                ]}
              />
              <Filtro
                etichetta="Stato"
                valore={stato}
                onCambia={(v) => imposta('stato', v)}
                opzioni={[
                  { valore: TUTTI, etichetta: 'Tutti' },
                  { valore: 'attivi', etichetta: 'Attivi' },
                  { valore: 'disattivati', etichetta: 'Disattivati' },
                ]}
              />
            </div>
            {visibili.length === 0 ? (
              <StatoVuoto titolo="Nessun test con questi filtri" />
            ) : (
              <Gruppo test={visibili} ss={ss} esegue={scrive} />
            )}
          </div>
        )}
      </Corpo>
    </>
  );
}

function Gruppo({ test, ss, esegue }: { test: TestInElenco[]; ss: string; esegue: boolean }) {
  const { righe, barra, inizio } = usePaginazione(test, PAGINA, PER_PAGINA);
  return (
    // Sotto la testata fissa del telefono, tornandoci cambiando pagina.
    <Card ref={inizio} className="scroll-mt-(--altezza-testata) bg-muted">
      <CardHeader>
        <CardTitle className="flex items-center justify-center gap-2 font-semibold tracking-wide uppercase">
          <RulerIcon aria-hidden className="size-5" />
          <h2>Test</h2>
          <Badge className="tabular-nums">{test.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4 px-2 @xl:px-(--card-spacing)">
        {/* Le righe affiancate sono alte uguali: il `li` a griglia allunga la riga. */}
        <ul className="grid gap-2 @5xl:grid-cols-2">
          {righe.map((t) => (
            <li key={t.id} className="grid">
              <RigaTest test={t} ss={ss} esegue={esegue} />
            </li>
          ))}
        </ul>
        {barra}
      </CardContent>
    </Card>
  );
}
